import { VisibilityState } from '@tanstack/react-table';
import { ColumnConfig, DataTableColumn, DataTableRow } from '../types';

/**
 * Load column configurations from localStorage
 *
 * A stored config outlives the code that produced it: columns get added and
 * removed between releases. New columns are appended (keeping the user's own
 * order for the ones they customized), and columns that no longer exist are
 * dropped — otherwise the customization dialog would keep listing them forever.
 *
 * Only `visible` and `order` are the user's; the label and description always
 * come from the live column definitions. Storing them froze the language they
 * were first rendered in, so the customization dialog kept showing English
 * headers long after the user switched language.
 */
export function loadColumnConfigs<T extends DataTableRow>(
  tableId: string,
  columns: DataTableColumn<T>[]
): ColumnConfig[] {
  const storageKey = `data_table_columns_${tableId}`;
  const saved = localStorage.getItem(storageKey);

  if (saved) {
    try {
      const savedConfigs = JSON.parse(saved) as ColumnConfig[];
      const columnById = new Map(columns.map((col) => [col.id, col]));
      const keptConfigs = savedConfigs
        .filter((config) => columnById.has(config.id))
        .map((config) => ({ ...config, ...labelOf(columnById.get(config.id)!) }));
      const savedIds = new Set(keptConfigs.map((c) => c.id));
      const lastOrder = keptConfigs.reduce((max, config) => Math.max(max, config.order), -1);

      const newColumns = columns
        .filter((col) => !savedIds.has(col.id))
        .map((col, index) => ({
          id: col.id,
          ...labelOf(col),
          visible: col.visible !== false,
          order: lastOrder + 1 + index
        }));

      // Re-index so `order` stays contiguous after pruning
      return [...keptConfigs, ...newColumns]
        .sort((a, b) => a.order - b.order)
        .map((config, index) => ({ ...config, order: index }));
    } catch (e) {
      console.error('Failed to parse saved columns', e);
    }
  }

  return columns.map((col, index) => ({
    id: col.id,
    ...labelOf(col),
    visible: col.visible !== false,
    order: index
  }));
}

/**
 * Save column configurations to localStorage
 */
export function saveColumnConfigs(tableId: string, configs: ColumnConfig[]): void {
  const storageKey = `data_table_columns_${tableId}`;
  // Persist the user's choices only — wording is resolved from the columns on load.
  const stored = configs.map(({ id, visible, order }) => ({ id, visible, order }));
  localStorage.setItem(storageKey, JSON.stringify(stored));
}

/**
 * Convert column configs to visibility state for tanstack table
 */
export function configsToVisibilityState(configs: ColumnConfig[]): VisibilityState {
  const visibility: VisibilityState = {};
  configs.forEach((config) => {
    visibility[config.id] = config.visible;
  });
  return visibility;
}

/**
 * Order columns based on configuration
 */
export function orderColumns<T extends DataTableRow>(
  columns: DataTableColumn<T>[],
  configs: ColumnConfig[]
): DataTableColumn<T>[] {
  const columnsMap = new Map(columns.map((col) => [col.id, col]));

  return [...configs]
    .sort((a, b) => a.order - b.order)
    .map((config) => columnsMap.get(config.id))
    .filter((col): col is DataTableColumn<T> => col !== undefined);
}

/**
 * Reorder column configs based on drag and drop
 */
export function reorderColumnConfigs(configs: ColumnConfig[], activeId: string, overId: string): ColumnConfig[] {
  const oldIndex = configs.findIndex((item) => item.id === activeId);
  const newIndex = configs.findIndex((item) => item.id === overId);

  if (oldIndex === -1 || newIndex === -1) {
    return configs;
  }

  const newConfigs = [...configs];
  const [movedItem] = newConfigs.splice(oldIndex, 1);
  newConfigs.splice(newIndex, 0, movedItem);

  return newConfigs.map((item, index) => ({ ...item, order: index }));
}

/**
 * Toggle column visibility in configs
 */
export function toggleColumnConfig(configs: ColumnConfig[], columnId: string): ColumnConfig[] {
  return configs.map((col) => (col.id === columnId ? { ...col, visible: !col.visible } : col));
}

/**
 * Create default column configs from columns
 */
export function createDefaultConfigs<T extends DataTableRow>(columns: DataTableColumn<T>[]): ColumnConfig[] {
  return columns.map((col, index) => ({
    id: col.id,
    ...labelOf(col),
    visible: col.visible !== false,
    order: index
  }));
}

/**
 * Re-attaches the current wording to configs that are held in state. State
 * survives a language switch; the labels captured when it was created must not.
 */
export function withColumnLabels<T extends DataTableRow>(
  configs: ColumnConfig[],
  columns: DataTableColumn<T>[]
): ColumnConfig[] {
  const columnById = new Map(columns.map((col) => [col.id, col]));
  return configs.map((config) => {
    const column = columnById.get(config.id);
    return column ? { ...config, ...labelOf(column) } : config;
  });
}

/** Wording always comes from the current render, never from storage. */
function labelOf<T extends DataTableRow>(col: DataTableColumn<T>) {
  return { label: typeof col.header === 'string' ? col.header : col.id, description: col.description || '' };
}
